import { Input as AntInput, notification, Table } from "antd";
import classnames from "classnames";
import cuid from "cuid";
import { get } from "lodash";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiInfoCircle, BiTrash } from "react-icons/bi";
import { MdAdd, MdOutlineSell } from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import Tooltip from "shared/components/Tooltip";
import { NAME_SPACES } from "shared/locales/constants";
import { useMutation, useQuery } from "shared/hooks/useApi";
import {
  ADD_ADVISOR_PRODUCT_SELLER_CODE,
  DELETE_ADVISOR_PRODUCT_SELLER_CODE,
  UPDATE_ADVISOR_PRODUCT_SELLER_CODE,
} from "utils/api/graphql/mutations/product-range-advisor-codes";
import {
  ADVISOR_PRODUCT_SELLER_CODES,
  PRODUCT_RANGES,
} from "utils/api/graphql/queries/product-range-advisor-codes";
import { USERS } from "utils/api/graphql/queries/users";
import { USER_TYPES } from "utils/constants";
import { resolveApiErrorMessage } from "utils/helpers/graphql-errors";

const ADVISOR_META_TYPES = new Set([
  USER_TYPES.INTERNAL_ADVISOR.type,
  USER_TYPES.EXTERNAL_ADVISOR.type,
  USER_TYPES.BROKER_EMPLOYEE.type,
  USER_TYPES.INTERNAL_SUPERVISOR.type,
]);

const TRANSLATION_KEY = "SELLER_CODE_MAPPINGS";

const MAPPINGS_TABLE_PAGE_SIZE = 5;

/** Masked value shown when a write-only password is already stored. */
const CONFIGURED_PASSWORD_MASK = "••••••••";

function AdvisorSellerCodeMappingsCard({
  organizationId,
  lockedUserId,
  nameSpace,
  showRangesHint,
  introMessage,
  showLabelTooltips,
}) {
  const { t } = useTranslation(nameSpace);
  const COPY = t(TRANSLATION_KEY, { returnObjects: true });
  const { t: tErrors } = useTranslation(NAME_SPACES.ERRORS);
  const apiErrorMessages = useMemo(
    () => tErrors("API", { returnObjects: true }),
    [tErrors],
  );

  const advisorSellerMappingOnError = useCallback(
    (apolloError) => {
      notification.error({
        message: resolveApiErrorMessage(apolloError, apiErrorMessages, {
          duplicateKeyMessageKey: "ADVISOR_PRODUCT_SELLER_DUPLICATE",
        }),
      });
    },
    [apiErrorMessages],
  );

  const fieldHintSuffix = (hintText) =>
    showLabelTooltips && hintText ? (
      <Tooltip title={hintText}>
        <BiInfoCircle size={14} className="c-gray" />
      </Tooltip>
    ) : null;

  const [addUserId, setAddUserId] = useState(null);
  const [addRangeId, setAddRangeId] = useState(null);
  const [addSellerCode, setAddSellerCode] = useState("");
  const [addApiPassword, setAddApiPassword] = useState("");
  const [editSellerCodes, setEditSellerCodes] = useState({});
  const [savingAllChanges, setSavingAllChanges] = useState(false);
  const [mappingsPage, setMappingsPage] = useState(1);

  const mappingsWhere = useMemo(() => {
    const base = { organization: { id: organizationId } };
    if (lockedUserId) {
      return { ...base, user: { id: lockedUserId } };
    }
    return base;
  }, [organizationId, lockedUserId]);

  const { data: rangesData } = useQuery(PRODUCT_RANGES, {
    variables: {
      where: { ownedBy: { id: organizationId } },
      take: 500,
      orderBy: [{ code: "ASC" }],
    },
    skip: !organizationId,
    fetchPolicy: "network-only",
  });

  const { data: mappingsData, refetch: refetchMappings } = useQuery(
    ADVISOR_PRODUCT_SELLER_CODES,
    {
      variables: {
        where: mappingsWhere,
        take: 500,
      },
      skip: !organizationId,
      fetchPolicy: "network-only",
    },
  );

  const { data: usersData } = useQuery(USERS, {
    variables: {
      where: { organization: { id: organizationId } },
      take: 500,
    },
    skip: !organizationId || Boolean(lockedUserId),
    fetchPolicy: "network-only",
  });

  const [addMapping] = useMutation(ADD_ADVISOR_PRODUCT_SELLER_CODE, {
    onCompleted: () => {
      refetchMappings();
      if (!lockedUserId) {
        setAddUserId(null);
      }
      setAddRangeId(null);
      setAddSellerCode("");
      setAddApiPassword("");
    },
    onError: advisorSellerMappingOnError,
  });
  const [updateMapping] = useMutation(UPDATE_ADVISOR_PRODUCT_SELLER_CODE, {
    onError: advisorSellerMappingOnError,
  });
  const [deleteMapping] = useMutation(DELETE_ADVISOR_PRODUCT_SELLER_CODE, {
    onCompleted: () => refetchMappings(),
  });

  const ranges = get(rangesData, "productRanges.data", []);
  const mappings = get(mappingsData, "advisorProductSellerCodes.data", []);
  const rawUsers = get(usersData, "users.data", []);

  useEffect(() => {
    const maxPage = Math.max(
      1,
      Math.ceil(mappings.length / MAPPINGS_TABLE_PAGE_SIZE),
    );
    setMappingsPage((prev) => Math.min(prev, maxPage));
  }, [mappings.length]);

  const advisorUsers = useMemo(
    () => rawUsers.filter((u) => ADVISOR_META_TYPES.has(get(u, "meta.type"))),
    [rawUsers],
  );

  const rangeOptions = useMemo(
    () =>
      ranges.map((r) => ({
        label: r.label ? `${r.code} — ${r.label}` : r.code,
        value: r.id,
      })),
    [ranges],
  );

  const userOptions = useMemo(
    () =>
      advisorUsers.map((u) => ({
        label:
          `${u.firstname || ""} ${u.lastname || ""}`.trim() || u.email || u.id,
        value: u.id,
      })),
    [advisorUsers],
  );

  const mappingsSyncKey = useMemo(
    () =>
      mappings
        .map((m) => `${m.id}:${m.sellerCode || ""}:${m.hasApiPassword ? 1 : 0}`)
        .join("|"),
    [mappings],
  );

  useEffect(() => {
    const next = {};
    mappings.forEach((m) => {
      next[m.id] = m.sellerCode || "";
    });
    setEditSellerCodes(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync on mapping identity only
  }, [mappingsSyncKey]);

  const effectiveUserId = lockedUserId || addUserId;

  async function handleAddMapping() {
    if (!effectiveUserId || !addRangeId || !organizationId) return;
    const apiPassword = (addApiPassword || "").trim();
    await addMapping({
      variables: {
        data: {
          id: cuid(),
          sellerCode: (addSellerCode || "").trim(),
          ...(apiPassword ? { apiPassword } : {}),
          user: { id: effectiveUserId },
          productRange: { id: addRangeId },
        },
      },
    });
  }

  const dirtyMappingRows = useMemo(
    () =>
      mappings.filter((m) => {
        const edited = (editSellerCodes[m.id] ?? "").trim();
        const original = (m.sellerCode || "").trim();
        return edited !== original;
      }),
    [mappings, editSellerCodes],
  );

  const hasDirtyMappingRows = dirtyMappingRows.length > 0;

  async function handleSaveAllMappingChanges() {
    if (!hasDirtyMappingRows || savingAllChanges) {
      return;
    }
    setSavingAllChanges(true);
    try {
      await Promise.all(
        dirtyMappingRows.map((m) =>
          updateMapping({
            variables: {
              where: { id: m.id },
              data: {
                sellerCode: (editSellerCodes[m.id] ?? "").trim(),
              },
            },
          }),
        ),
      );
      await refetchMappings();
    } finally {
      setSavingAllChanges(false);
    }
  }

  async function handleDeleteMapping(rowId) {
    await deleteMapping({ variables: { where: { id: rowId } } });
  }

  if (!organizationId) {
    return null;
  }

  const hintVisible =
    showRangesHint &&
    Boolean(COPY.RANGES_ADMIN_HINT && String(COPY.RANGES_ADMIN_HINT).trim());
  const introVisible =
    Boolean(introMessage && String(introMessage).trim()) && !hintVisible;

  const advisorCell = (record) =>
    `${get(record, "user.firstname", "")} ${get(
      record,
      "user.lastname",
      "",
    )}`.trim() || get(record, "user.id");

  const gammeCell = (record) =>
    get(record, "productRange.label") || get(record, "productRange.code") || "";

  const mappingColumns = [];

  if (!lockedUserId) {
    mappingColumns.push({
      title: COPY.ADVISOR_LABEL,
      key: "advisor",
      render: (_, record) => advisorCell(record),
    });
  }

  mappingColumns.push(
    {
      title: COPY.GAMME_LABEL,
      key: "range",
      render: (_, record) => gammeCell(record),
    },
    {
      title: COPY.SELLER_CODE_LABEL,
      key: "sellerCode",
      render: (_, record) => (
        <AntInput
          size="large"
          className="w-max"
          value={editSellerCodes[record.id] ?? ""}
          onChange={(e) =>
            setEditSellerCodes((prev) => ({
              ...prev,
              [record.id]: e.target.value,
            }))
          }
        />
      ),
    },
    {
      title: COPY.API_PASSWORD_LABEL,
      key: "apiPassword",
      render: (_, record) => {
        const isConfigured = Boolean(record.hasApiPassword);
        return (
          <AntInput
            size="large"
            className={classnames(
              "w-max",
              "seller-code-mappings__api-password",
              {
                "seller-code-mappings__api-password--configured": isConfigured,
              },
            )}
            disabled
            readOnly
            autoComplete="new-password"
            value={isConfigured ? CONFIGURED_PASSWORD_MASK : ""}
            placeholder={
              isConfigured
                ? COPY.API_PASSWORD_SET_PLACEHOLDER
                : COPY.API_PASSWORD_NOT_SET_PLACEHOLDER
            }
          />
        );
      },
    },
    {
      title: COPY.ACTIONS,
      key: "actions",
      align: "right",
      render: (_, record) => (
        <div className="d-flex align--center gap-8 justify--end flex--wrap">
          <Button
            type="danger--outlined"
            onClick={() => handleDeleteMapping(record.id)}
          >
            <BiTrash />
          </Button>
        </div>
      ),
    },
  );

  return (
    <div className="company">
      <div className="company__space-y-10">
        {hintVisible ? (
          <p className="company__text-sm company__text-slate-600 company__mb-4">
            {COPY.RANGES_ADMIN_HINT}
          </p>
        ) : null}
        {introVisible ? (
          <p className="company__text-sm company__text-slate-600 company__mb-4">
            {introMessage}
          </p>
        ) : null}

        <div>
          <h3 className="company__text-lg company__font-semibold company__mb-4 d-flex align--center gap-8">
            <MdOutlineSell className="company__w-5 company__h-5" />
            {COPY.MAPPINGS_TITLE}
          </h3>
          <p className="company__text-sm company__text-slate-600 company__mb-4">
            {COPY.MAPPINGS_HELP}
          </p>

          <Table
            className="table__wrapper m-bottom-16"
            dataSource={mappings}
            rowKey="id"
            columns={mappingColumns}
            pagination={{
              current: mappingsPage,
              pageSize: MAPPINGS_TABLE_PAGE_SIZE,
              total: mappings.length,
              showSizeChanger: false,
              hideOnSinglePage: true,
              onChange: (page) => setMappingsPage(page),
            }}
            scroll={{ x: true }}
            locale={{ emptyText: COPY.EMPTY_MAPPINGS }}
          />

          {mappings.length > 0 ? (
            <div className="d-flex align--center gap-16 m-bottom-24 flex--wrap">
              <Button
                type="primary"
                onClick={handleSaveAllMappingChanges}
                disabled={!hasDirtyMappingRows}
                loading={savingAllChanges}
              >
                {COPY.SAVE_ALL_CHANGES}
              </Button>
            </div>
          ) : null}

          <div
            className={
              lockedUserId ? "grid--3 gap-16 mt-24" : "grid--4 gap-16 mt-24"
            }
          >
            {lockedUserId ? null : (
              <Select
                label={COPY.ADVISOR_LABEL}
                labelSuffix={fieldHintSuffix(COPY.ADVISOR_FIELD_HELP)}
                placeholder={COPY.ADVISOR_PLACEHOLDER}
                options={userOptions}
                value={addUserId}
                onChange={setAddUserId}
                showSearch
                optionFilterProp="label"
              />
            )}
            <Select
              label={COPY.GAMME_LABEL}
              labelSuffix={fieldHintSuffix(COPY.GAMME_FIELD_HELP)}
              placeholder={COPY.GAMME_PLACEHOLDER}
              options={rangeOptions}
              value={addRangeId}
              onChange={setAddRangeId}
              showSearch
              optionFilterProp="label"
            />
            <Input
              type="simple"
              label={COPY.SELLER_CODE_LABEL}
              labelSuffix={fieldHintSuffix(COPY.SELLER_CODE_FIELD_HELP)}
              placeholder={COPY.SELLER_CODE_PLACEHOLDER}
              value={addSellerCode}
              onChange={(e) => setAddSellerCode(e.target.value)}
            />
            <Input
              type="password"
              label={COPY.API_PASSWORD_LABEL}
              labelSuffix={fieldHintSuffix(COPY.API_PASSWORD_FIELD_HELP)}
              placeholder={COPY.API_PASSWORD_PLACEHOLDER}
              autoComplete="new-password"
              value={addApiPassword}
              onChange={(e) => setAddApiPassword(e.target.value)}
            />
          </div>
          <Button
            type="primary"
            className="mt-16"
            onClick={handleAddMapping}
            disabled={!effectiveUserId || !addRangeId}
          >
            <MdAdd className="company__w-5 company__h-5" />
            {COPY.ADD_MAPPING}
          </Button>
        </div>
      </div>
    </div>
  );
}

AdvisorSellerCodeMappingsCard.propTypes = {
  organizationId: PropTypes.string,
  lockedUserId: PropTypes.string,
  nameSpace: PropTypes.string.isRequired,
  showRangesHint: PropTypes.bool,
  introMessage: PropTypes.string,
  showLabelTooltips: PropTypes.bool,
};

AdvisorSellerCodeMappingsCard.defaultProps = {
  organizationId: null,
  lockedUserId: null,
  showRangesHint: false,
  introMessage: null,
  showLabelTooltips: false,
};

export default AdvisorSellerCodeMappingsCard;
